import { calculateThumbnailDimensions } from '@app/core/thumbnails/utils';
import { GraphScale } from '@mi-front/core';
import { useMemo } from 'react';
import { TimestampedThumbnail } from './types';

export interface ThumbnailLayoutV2 {
  thumbnail: TimestampedThumbnail;
  width: number;
  x: number;
  spriteWidth: number;
}

interface UseThumbnailLayoutV2Params {
  thumbnails: TimestampedThumbnail[];
  scale: GraphScale;
  thumbHeight: number;
  containerWidth: number;
}

/**
 * Minimum fraction of the previous frame width that must separate consecutive
 * visible thumbnails. Values below 1 allow slight overlap on short segments.
 */
const MIN_FRAME_SPACING_RATIO = 0.85;

/**
 * Builds a time-aligned filmstrip layout: each thumbnail is placed at
 * scale(startTs) with width derived from sprite aspect ratio (frameWidth).
 * Thumbnails are subsampled when their start positions would be too close together.
 */
export function useThumbnailLayoutV2({
  thumbnails,
  scale,
  thumbHeight,
  containerWidth,
}: UseThumbnailLayoutV2Params): ThumbnailLayoutV2[] {
  return useMemo(() => {
    if (!thumbnails.length || !containerWidth) return [];

    const sorted = [...thumbnails].sort((a, b) => a.startTs - b.startTs);
    const layouts: ThumbnailLayoutV2[] = [];
    let lastKeptX = -Infinity;
    let lastKeptWidth = 0;

    for (const thumbnail of sorted) {
      if (!thumbnail.metadata) {
        continue;
      }

      const scaledX = scale(thumbnail.startTs);
      if (scaledX == null || Number.isNaN(scaledX)) continue;

      const { frameWidth, spriteWidth } = calculateThumbnailDimensions(
        thumbnail.metadata,
        thumbHeight,
      );

      // Skip thumbnails fully outside the visible container
      if (scaledX + frameWidth < 0 || scaledX > containerWidth) continue;

      const minGap = lastKeptWidth * MIN_FRAME_SPACING_RATIO;
      const isFirst = layouts.length === 0;
      if (!isFirst && scaledX - lastKeptX < minGap) continue;

      layouts.push({
        thumbnail,
        width: frameWidth,
        x: scaledX,
        spriteWidth,
      });

      lastKeptX = scaledX;
      lastKeptWidth = frameWidth;
    }

    return layouts;
  }, [thumbnails, scale, thumbHeight, containerWidth]);
}
